import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import type { Permission } from '@/types/auth';

interface RequirePermissionProps {
  permission?: Permission | Permission[];
  // Role gate is separate from permission — e.g. SuperAdmin-only screens (cross-tenant company
  // management) shouldn't open just because an ordinary company Administrator also happens to
  // hold the same permission code for managing their own company.
  role?: string;
  children: ReactNode;
}

/** Route guard: redirects to /login if unauthenticated, /unauthorized if missing the permission/role. */
export function RequirePermission({ permission, role, children }: RequirePermissionProps) {
  const { isAuthenticated, isLoading, hasPermission, hasRole } = useAuth();
  const location = useLocation();

  if (isLoading) return null;

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (permission && !hasPermission(permission)) {
    return <Navigate to="/unauthorized" replace />;
  }

  if (role && !hasRole(role)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
}

/** Element-level guard: renders nothing (rather than redirecting) when the permission/role is missing. */
export function IfPermitted({ permission, role, children }: RequirePermissionProps) {
  const { hasPermission, hasRole } = useAuth();
  if (permission && !hasPermission(permission)) return null;
  if (role && !hasRole(role)) return null;
  return <>{children}</>;
}
